import { useCallback } from 'react';
import { logError } from 'utils/logger';
import { useDispatch } from 'react-redux';
import { useMutation } from '@apollo/client';
import { useNotifications } from 'modules/notification';
import { PortalConfiguration } from '@trnsact/trnsact-shared-types';

import { portalConfigurationApi } from './index';
import { portalConfigurationSchema } from '../model/schemas';
import { portalConfigurationsActions } from '../../../redux/portalConfigurationReducer';
import { SavePortalConfigurationResponse, UpdatePortalConfigurationVariables } from './types';

export function usePortalConfiguration() {
  const { showNotification } = useNotifications();
  const dispatch = useDispatch();

  const [savePortalConfiguration, { loading: isPortalConfigurationSaving }] = useMutation<
    SavePortalConfigurationResponse,
    UpdatePortalConfigurationVariables
  >(portalConfigurationApi.saveConfig);

  const handleSaveConfiguration = useCallback(
    async (configToSave: PortalConfiguration) => {
      const { success } = portalConfigurationSchema.safeParse(configToSave);

      if (!success) {
        showNotification('Not saved! Issue with data for save!', { type: 'error' });
        return;
      }

      try {
        const { data } = await savePortalConfiguration({ variables: { input: configToSave } });

        configToSave.portalConfigurationId = data?.savePortalConfiguration;
        dispatch(portalConfigurationsActions.setConfigData([configToSave]));

        showNotification('Saved!');
      } catch (error) {
        showNotification('Error!', { type: 'error' });
        logError(error);
      }
    },
    [savePortalConfiguration, dispatch, showNotification]
  );

  return {
    handleSaveConfiguration,
    isPortalConfigurationSaving,
  };
}
